import { useState, useEffect } from 'react';
import { keyDatesApi, propertiesApi, formatDate, getDaysUntil, getUrgencyLevel, DATE_TYPES } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar, Plus, Pencil, Trash2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const getDateTypeLabel = (value) => {
  const found = DATE_TYPES.find(t => t.value === value);
  return found ? found.label : value?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const getUrgencyBadge = (dateString) => {
  const level = getUrgencyLevel(dateString);
  const days = getDaysUntil(dateString);

  const variants = {
    overdue: { className: 'bg-red-100 text-red-700 border-red-200', label: 'Overdue' },
    critical: { className: 'bg-red-100 text-red-700 border-red-200', label: `${days}d` },
    warning: { className: 'bg-amber-100 text-amber-700 border-amber-200', label: `${days}d` },
    safe: { className: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: `${days}d` },
  };

  return (
    <Badge variant="outline" className={variants[level].className}>
      {variants[level].label}
    </Badge>
  );
};

export const UpcomingDatesWidget = () => {
  const [dates, setDates] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDate, setEditingDate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    date_type: '',
    due_date: '',
    notes: '',
    property_id: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [datesRes, propsRes] = await Promise.all([
        keyDatesApi.getAll(),
        propertiesApi.getAll('active'),
      ]);
      // Sort chronologically
      const sorted = datesRes.data.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
      setDates(sorted);
      setProperties(propsRes.data);
    } catch (error) {
      console.error('Failed to fetch dates:', error);
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingDate(null);
    setFormData({ date_type: '', due_date: '', notes: '', property_id: '' });
    setModalOpen(true);
  };

  const openEditModal = (date) => {
    setEditingDate(date);
    setFormData({
      date_type: date.date_type || '',
      due_date: date.due_date || '',
      notes: date.notes || '',
      property_id: date.property_id || '',
    });
    setModalOpen(true);
  };

  const handleDelete = async (dateId) => {
    try {
      await keyDatesApi.delete(dateId);
      toast.success('Date deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete date');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.property_id) {
      toast.error('Please select a property');
      return;
    }
    if (!formData.date_type) {
      toast.error('Please select a date type');
      return;
    }
    if (!formData.due_date) {
      toast.error('Please select a date');
      return;
    }

    setSaving(true);
    try {
      if (editingDate) {
        await keyDatesApi.update(editingDate.id, {
          date_type: formData.date_type,
          due_date: formData.due_date,
          notes: formData.notes.trim(),
        });
        toast.success('Date updated');
      } else {
        await keyDatesApi.create({
          property_id: formData.property_id,
          date_type: formData.date_type,
          due_date: formData.due_date,
          notes: formData.notes.trim(),
        });
        toast.success('Date added');
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save date');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card className="border-slate-200" data-testid="upcoming-dates-widget">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold font-display flex items-center gap-2">
              <Calendar className="w-5 h-5 text-slate-500" />
              Upcoming Dates
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={openAddModal}
              className="h-8 text-slate-600 hover:text-slate-900"
              data-testid="add-date-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="spinner w-6 h-6"></div>
            </div>
          ) : dates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Clock className="w-10 h-10 text-slate-300 mb-3" />
              <p className="text-sm text-slate-500">No upcoming dates</p>
              <p className="text-xs text-slate-400 mt-1">Add key dates to track important deadlines</p>
              <Button
                variant="outline"
                size="sm"
                onClick={openAddModal}
                className="mt-3"
                data-testid="add-first-date-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Date
              </Button>
            </div>
          ) : (
            <div className="space-y-2" data-testid="upcoming-dates-list">
              {dates.map((date) => (
                <div
                  key={date.id}
                  className="flex items-start justify-between p-3 bg-slate-50 rounded-lg group hover:bg-slate-100 transition-colors"
                  data-testid={`date-item-${date.id}`}
                >
                  <div className="flex-1 min-w-0 mr-3">
                    <p className="text-sm font-medium text-slate-900">
                      {getDateTypeLabel(date.date_type)}
                    </p>
                    <p className="text-xs text-slate-500 truncate mt-0.5">
                      {date.property_address}
                    </p>
                    {date.notes && (
                      <p className="text-xs text-slate-400 mt-0.5 truncate">{date.notes}</p>
                    )}
                    <p className="text-xs text-slate-400 mt-1">
                      {formatDate(date.due_date)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {getUrgencyBadge(date.due_date)}
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => openEditModal(date)}
                        className="p-1 rounded hover:bg-slate-200 transition-colors"
                        data-testid={`edit-date-${date.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5 text-slate-500" />
                      </button>
                      <button
                        onClick={() => handleDelete(date.id)}
                        className="p-1 rounded hover:bg-red-100 transition-colors"
                        data-testid={`delete-date-${date.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editingDate ? 'Edit Key Date' : 'Add Key Date'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Property */}
            <div>
              <Label>Property *</Label>
              <Select
                value={formData.property_id}
                onValueChange={(value) => setFormData({ ...formData, property_id: value })}
                disabled={saving || !!editingDate}
              >
                <SelectTrigger className="mt-1.5" data-testid="date-property-select">
                  <SelectValue placeholder="Select property..." />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.address.split(',')[0].trim()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Type */}
            <div>
              <Label>Date Type *</Label>
              <Select
                value={formData.date_type}
                onValueChange={(value) => setFormData({ ...formData, date_type: value })}
                disabled={saving}
              >
                <SelectTrigger className="mt-1.5" data-testid="date-type-select">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {DATE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Due Date */}
            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="mt-1.5"
                disabled={saving}
                data-testid="date-due-input"
              />
            </div>

            {/* Notes */}
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any notes about this date..."
                className="mt-1.5 resize-none"
                rows={2}
                disabled={saving}
                data-testid="date-notes-input"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setModalOpen(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-slate-900 hover:bg-slate-800"
                disabled={saving}
                data-testid="date-submit-btn"
              >
                {saving ? 'Saving...' : editingDate ? 'Save Changes' : 'Add Date'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UpcomingDatesWidget;
